import React from 'react';

import { mdiCog, mdiFileDocumentEditOutline } from '@lumapps/lumx/icons';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { SETUP_SIDEBAR_ITEMS } from '../constants';
import { MICRO_APP_PICKER } from '../keys';
import { Menus } from '../types';

/**
 * Use this hook to handle sidebar navigation for microapp setup dialog.
 */
export const useNavigation = (defaultActiveKey: Menus) => {
    const { translateKey } = useTranslate();

    // Stores menu key. Useful for `analytics` for example.
    const [selectedMenuKey, setSelectedMenuKey] = React.useState<Menus | undefined>(defaultActiveKey);
    // Stores opened menu keys. Since you can open multiple menus, it has to be an array.
    const [openedMenuKeys, setOpenedMenuKeys] = React.useState(Object.keys(SETUP_SIDEBAR_ITEMS) as Menus[]);

    /** Toggle a menu */
    const toggleMenu = React.useCallback((menuKey: Menus) => {
        setOpenedMenuKeys((prev) => {
            if (prev.includes(menuKey)) {
                return [...prev.filter((k) => k !== menuKey)];
            }

            return [...prev, menuKey];
        });
    }, []);

    /**
     * Action to execute when user opens a menu.
     * Basically stores necessary information.
     */
    const activateMenuKey = React.useCallback((key: Menus) => {
        setSelectedMenuKey(key);
    }, []);

    /**
     * Callback to execute when user opens a menu.
     */
    const onMenuPress = React.useCallback(
        (menu: Menus) => {
            activateMenuKey(menu);
            toggleMenu(menu);
        },
        [activateMenuKey, toggleMenu],
    );

    /** Used to get different icons for the menu */
    const getMenuIcon = React.useCallback((menu: Menus) => {
        switch (menu) {
            case 'settings':
                return mdiCog;
            case 'inputVariables':
                return mdiFileDocumentEditOutline;
            default:
                return undefined;
        }
    }, []);

    /** Using this method to avoid rewriting all menu object (and subsequent method and typings) */
    const getTranslatedKey = React.useCallback(
        (menu: Menus) => {
            switch (menu) {
                case 'settings':
                    return translateKey(GLOBAL.SETTINGS);
                case 'inputVariables':
                    return translateKey(MICRO_APP_PICKER.INPUT_VARIABLES);
                default:
                    return menu;
            }
        },
        [translateKey],
    );

    return {
        activateSubmenuKey: activateMenuKey,
        activeMenuKey: selectedMenuKey,
        menus: SETUP_SIDEBAR_ITEMS,
        openedMenuKeys,
        onMenuPress,
        getMenuIcon,
        getTranslatedKey,
    };
};
